import React, { useState, useEffect } from 'react';
import { FaCheckCircle, FaTimesCircle, FaFileExcel } from 'react-icons/fa';
import { Order } from '../../types';
import styles from './styles/OrdersList.module.css';
import vStyles from './styles/VerificacionesCrud.module.css';

interface VerificationOrder extends Order {
    Banco?: string;
    Verificado?: boolean;  // Campo separado de Pagado, exclusivo de la contadora
}

// Tabs por FORMA DE COBRO (lo importante en verificaciones es como entro la plata).
type PayTab = 'Efectivo' | 'Transferencia' | 'Debito' | 'Credito' | 'QR' | 'MercadoPago' | 'Mixto' | 'Todas';

const PAY_TABS: { key: PayTab; label: string }[] = [
    { key: 'Efectivo', label: 'Efectivo' },
    { key: 'Transferencia', label: 'Transferencia' },
    { key: 'Debito', label: 'Débito' },
    { key: 'Credito', label: 'Crédito' },
    { key: 'QR', label: 'QR' },
    { key: 'MercadoPago', label: 'MercadoPago' },
    { key: 'Mixto', label: 'Mixto' },
    { key: 'Todas', label: 'Todas' },
];

// Parsea fechas YYYY-MM-DD como fecha LOCAL para evitar desfasaje UTC-3
const formatLocalDate = (dateStr: string): string => {
    if (!dateStr) return '-';
    const isoDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(dateStr);
    if (isoDateOnly) {
        const [year, month, day] = dateStr.split('-');
        return `${day}/${month}/${year}`;
    }
    return new Date(dateStr).toLocaleDateString('es-AR');
};

const VerificacionesCrud: React.FC = () => {
    const [orders, setOrders] = useState<VerificationOrder[]>([]);
    const [loading, setLoading] = useState(true);
    const [expandedOrder, setExpandedOrder] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<PayTab>('Todas');

    // Filtros (mismos que Pedidos)
    const [filterDateStart, setFilterDateStart] = useState('');
    const [filterDateEnd, setFilterDateEnd] = useState('');
    const [filterClient, setFilterClient] = useState('');
    const [filterStatus, setFilterStatus] = useState('Todos'); // estado de verificacion
    const [filterLocal, setFilterLocal] = useState('Todos');
    const [locales, setLocales] = useState<any[]>([]);

    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

    useEffect(() => {
        fetchLocales();
        fetchOrders();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchLocales = async () => {
        try {
            const res = await fetch(`${API_URL}/locales`);
            if (res.ok) setLocales(await res.json());
        } catch (e) { console.error(e); }
    };

    const fetchOrders = () => {
        setLoading(true);
        const params = new URLSearchParams();
        if (filterDateStart) params.append('fecha_inicio', filterDateStart);
        if (filterDateEnd) params.append('fecha_fin', filterDateEnd);
        if (filterClient) params.append('cliente', filterClient);
        if (filterLocal && filterLocal !== 'Todos') params.append('id_local', filterLocal);
        params.append('tipo_tab', 'Todas'); // Verificaciones ve todos los tipos de pedido

        fetch(`${API_URL}/admin/orders?${params.toString()}`)
            .then(res => res.json())
            .then(data => {
                setOrders(Array.isArray(data) ? data : []);
                setLoading(false);
            })
            .catch(err => {
                console.error(err);
                setLoading(false);
            });
    };

    const markVerificado = async (orderId: string, verificado: boolean) => {
        try {
            const res = await fetch(`${API_URL}/admin/verificaciones/mark-verificado`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ order_id: orderId, verificado }),
            });
            if (res.ok) {
                setOrders(prev => prev.map(o => (o.ID === orderId ? { ...o, Verificado: verificado } : o)));
            }
        } catch (e) {
            console.error(e);
        }
    };

    const toggleExpand = (id: string) => setExpandedOrder(prev => prev === id ? null : id);

    // ── Desglose por forma de cobro ──────────────────────────────────────
    // Un pago Mixto solo aparece en el tab "Mixto" (con su detalle).
    const matchesPayTab = (order: VerificationOrder): boolean => {
        const m = (order.MetodoPago || '').toLowerCase();
        switch (activeTab) {
            case 'Todas': return true;
            case 'Mixto': return m === 'mixto';
            case 'Efectivo': return m === 'efectivo';
            case 'Transferencia': return m === 'transferencia';
            case 'Debito': return m === 'debito' || m === 'débito';
            case 'Credito': return m === 'credito' || m === 'crédito';
            case 'QR': return m === 'qr';
            case 'MercadoPago': return m === 'mercadopago';
            default: return true;
        }
    };

    const matchesStatus = (order: VerificationOrder): boolean => {
        if (filterStatus === 'Todos') return true;
        if (filterStatus === 'Verificado') return !!order.Verificado;
        if (filterStatus === 'SinVerificar') return !order.Verificado;
        return true;
    };

    const filteredOrders = orders.filter(o => matchesPayTab(o) && matchesStatus(o));

    // Detalle legible de un pago mixto
    const renderFormaCobro = (order: VerificationOrder) => {
        if ((order.MetodoPago || '').toLowerCase() === 'mixto' && order.pagos_mixtos?.length) {
            return (
                <div>
                    <strong>Mixto</strong>
                    <div className={vStyles['sub-info']}>
                        {order.pagos_mixtos.map((p, i) => (
                            <div key={i}>{p.metodo}: ${Number(p.monto).toLocaleString('es-AR')}</div>
                        ))}
                    </div>
                </div>
            );
        }
        return <strong>{order.MetodoPago || '—'}</strong>;
    };

    const exportToCSV = () => {
        if (filteredOrders.length === 0) return;
        const BOM = '﻿';
        const esc = (v: any) => `"${String(v ?? '').replace(/"/g, '""')}"`;
        const headers = ['N° Pedido', 'Fecha', 'Cliente', 'Tipo', 'Forma de Cobro', 'Detalle Mixto', 'Banco', 'Monto', 'Verificado'];
        const rows = filteredOrders.map(o => [
            o.ID,
            o.FechaEntrega ? formatLocalDate(o.FechaEntrega) : formatLocalDate(o.Fecha),
            esc(o.Cliente),
            esc(o.TipoPedido),
            esc(o.MetodoPago),
            esc((o.MetodoPago || '').toLowerCase() === 'mixto' && o.pagos_mixtos
                ? o.pagos_mixtos.map(p => `${p.metodo}: ${p.monto}`).join(' | ') : ''),
            esc(o.Banco || ''),
            Number(o.Total),
            o.Verificado ? 'Verificado' : 'Sin verificar',
        ]);
        const csv = BOM + [headers.join(';'), ...rows.map(r => r.join(';'))].join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `verificaciones_${activeTab.toLowerCase()}_${filterDateStart || 'inicio'}_${filterDateEnd || 'hoy'}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const clearFilters = () => {
        setFilterDateStart('');
        setFilterDateEnd('');
        setFilterClient('');
        setFilterStatus('Todos');
        setFilterLocal('Todos');
        setTimeout(fetchOrders, 0);
    };

    if (loading) return <div>Cargando verificaciones...</div>;

    return (
        <div className={styles['orders-container']}>
            {/* Tabs por forma de cobro */}
            <div className={styles['orders-tabs']}>
                {PAY_TABS.map(t => (
                    <button
                        key={t.key}
                        className={`${styles['tab-btn']} ${activeTab === t.key ? styles.active : ''}`}
                        onClick={() => setActiveTab(t.key)}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            {/* Barra de filtros (igual a Pedidos) */}
            <div className={styles['filters-bar']}>
                <div className={styles['filter-group']}>
                    <label>Local</label>
                    <select
                        className={styles['filter-input']}
                        value={filterLocal}
                        onChange={(e) => setFilterLocal(e.target.value)}
                    >
                        <option value="Todos">Todos los Locales</option>
                        {locales.map(l => (
                            <option key={l.locales_id} value={l.locales_id}>{l.nombre_local}</option>
                        ))}
                    </select>
                </div>
                <div className={styles['filter-group']}>
                    <label>Desde</label>
                    <input
                        type="date"
                        className={styles['filter-input']}
                        value={filterDateStart}
                        onChange={(e) => setFilterDateStart(e.target.value)}
                    />
                </div>
                <div className={styles['filter-group']}>
                    <label>Hasta</label>
                    <input
                        type="date"
                        className={styles['filter-input']}
                        value={filterDateEnd}
                        onChange={(e) => setFilterDateEnd(e.target.value)}
                    />
                </div>
                <div className={styles['filter-group']}>
                    <label>Cliente / ID</label>
                    <input
                        type="text"
                        placeholder="Nombre, DNI o N° pedido"
                        className={styles['filter-input']}
                        value={filterClient}
                        onChange={(e) => setFilterClient(e.target.value)}
                    />
                </div>
                <div className={styles['filter-group']}>
                    <label>Verificación</label>
                    <select
                        className={styles['filter-input']}
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                    >
                        <option value="Todos">Todos</option>
                        <option value="Verificado">Verificado</option>
                        <option value="SinVerificar">Sin verificar</option>
                    </select>
                </div>
                <button className={styles['filter-btn']} onClick={fetchOrders}>
                    Filtrar
                </button>
                <button className={`${styles['filter-btn']} ${styles['btn-clear']}`} onClick={clearFilters}>
                    Limpiar
                </button>
                <button
                    onClick={exportToCSV}
                    disabled={filteredOrders.length === 0}
                    title={`Exportar ${filteredOrders.length} verificaciones a Excel`}
                    style={{
                        display: 'flex', alignItems: 'center', gap: '6px',
                        background: '#1d6f42', color: '#fff', border: 'none',
                        borderRadius: '6px', padding: '6px 14px', cursor: 'pointer',
                        fontSize: '0.85rem', fontWeight: 600,
                        opacity: filteredOrders.length === 0 ? 0.4 : 1,
                    }}
                >
                    <FaFileExcel /> Exportar ({filteredOrders.length})
                </button>
            </div>

            <div className={styles['orders-table-container']}>
                <table className={styles['orders-table']}>
                    <thead>
                        <tr>
                            <th>N° Pedido</th>
                            <th>Fecha</th>
                            <th>Cliente</th>
                            <th>Forma de Cobro</th>
                            <th>Banco</th>
                            <th>Monto</th>
                            <th>Verificación</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredOrders.length === 0 ? (
                            <tr>
                                <td colSpan={8} style={{ textAlign: 'center', padding: '2rem' }}>
                                    No hay cobros que coincidan con los filtros.
                                </td>
                            </tr>
                        ) : (
                            filteredOrders.map(order => {
                                const isVerified = !!order.Verificado;
                                return (
                                    <React.Fragment key={order.ID}>
                                        <tr className={isVerified ? vStyles['row-verified'] : ''}>
                                            <td><strong>#{order.ID}</strong></td>
                                            <td>
                                                {order.FechaEntrega ? (
                                                    <span>
                                                        {formatLocalDate(order.FechaEntrega)}
                                                        {order.HorarioEntrega && <> · <strong>{order.HorarioEntrega}</strong></>}
                                                        <br />
                                                        <small style={{ color: '#888', fontSize: '0.75em' }}>
                                                            {order.TipoPedido === 'Envio' || order.TipoPedido === 'Venta Web'
                                                                ? '🚚 Envío'
                                                                : order.TipoPedido === 'Retiro'
                                                                    ? '🏪 Retiro'
                                                                    : '⚡ Inmediato'}
                                                        </small>
                                                    </span>
                                                ) : (
                                                    <span>
                                                        {formatLocalDate(order.Fecha)}
                                                        <br />
                                                        <small style={{ color: '#888', fontSize: '0.75em' }}>⚡ Inmediato</small>
                                                    </span>
                                                )}
                                            </td>
                                            <td>
                                                <strong>{order.Cliente}</strong><br />
                                                <small>{order.Contacto}</small>
                                            </td>
                                            <td>{renderFormaCobro(order)}</td>
                                            <td>{order.Banco || <span style={{ opacity: 0.3 }}>—</span>}</td>
                                            <td>
                                                ${Number(order.Total).toLocaleString('es-AR')}
                                                {order.CostoEnvio && order.CostoEnvio > 0 && (
                                                    <div className={vStyles['shipping-cost']}>
                                                        + ${order.CostoEnvio.toLocaleString('es-AR')} envío
                                                    </div>
                                                )}
                                            </td>
                                            <td>
                                                <span className={`${vStyles['status-badge']} ${isVerified ? vStyles['status-pagado'] : vStyles['status-pendiente']}`}>
                                                    {isVerified ? <FaCheckCircle /> : <FaTimesCircle />}
                                                    {isVerified ? 'VERIFICADO' : 'SIN VERIFICAR'}
                                                </span>
                                            </td>
                                            <td>
                                                <div style={{ display: 'flex', gap: '0.5rem' }}>
                                                    <button
                                                        className={`${styles['btn-action']} ${styles['btn-details']}`}
                                                        onClick={() => toggleExpand(order.ID)}
                                                        title="Ver Detalle"
                                                    >
                                                        {expandedOrder === order.ID ? 'Ocultar' : 'Detalle'}
                                                    </button>
                                                    <button
                                                        className={`${vStyles['btn-action']} ${isVerified ? vStyles['btn-unverify'] : vStyles['btn-verify']}`}
                                                        onClick={() => markVerificado(order.ID, !isVerified)}
                                                    >
                                                        {isVerified ? 'Desmarcar' : 'Verificar'}
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                        {expandedOrder === order.ID && (
                                            <tr className={styles['order-details-row']}>
                                                <td colSpan={8}>
                                                    <div className={styles['details-content']}>
                                                        <div className={styles['details-block']}>
                                                            <h4>Productos</h4>
                                                            <div className={styles['details-list']}>
                                                                <ul>
                                                                    {order.Items.map((item, i) => (
                                                                        <li key={i}>
                                                                            {item.Producto} <strong>x{item.Cantidad}</strong>
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                            </div>
                                                        </div>
                                                        <div className={styles['details-block']}>
                                                            <h4>Datos del cobro</h4>
                                                            <p><strong>Forma de cobro:</strong> {order.MetodoPago}</p>
                                                            {order.Banco && <p><strong>Banco:</strong> {order.Banco}</p>}
                                                            <p><strong>Estado de pago:</strong> {order.Pagado === 'Si' ? 'Pagado' : 'Pendiente'}</p>
                                                            {(order.MetodoPago || '').toLowerCase() === 'mixto' && order.pagos_mixtos?.length && (
                                                                <p>
                                                                    <strong>Detalle mixto:</strong>{' '}
                                                                    {order.pagos_mixtos.map((p, i) => (
                                                                        <span key={i}>{p.metodo}: ${Number(p.monto).toLocaleString('es-AR')}{i < order.pagos_mixtos!.length - 1 ? ' · ' : ''}</span>
                                                                    ))}
                                                                </p>
                                                            )}
                                                            {order.Observaciones && <p><strong>Observaciones:</strong> {order.Observaciones}</p>}
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default VerificacionesCrud;
